import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static targets = ["icon", "text", "option"]
  static values = {
    preference: { type: String, default: "system" }
  }
  static VALID_PREFERENCES = Object.freeze(["light", "dark", "system"])
  static THEME_LABELS = Object.freeze({ light: "Light", dark: "Dark", system: "Auto" })

  connect() {
    this.applyTheme()
    this.updateUI()
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.handleSystemChange = this.handleSystemChange.bind(this)
    this.mediaQuery.addEventListener("change", this.handleSystemChange)
  }

  disconnect() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleSystemChange)
    }
  }

  toggle() {
    const currentTheme = this.getCurrentTheme()
    const newTheme = currentTheme === "dark" ? "light" : "dark"
    this.setPreference(newTheme)
  }

  setLight() {
    this.setPreference("light")
  }

  setDark() {
    this.setPreference("dark")
  }

  setSystem() {
    this.setPreference("system")
  }

  setPreference(preference) {
    if (!this.constructor.VALID_PREFERENCES.includes(preference)) return

    try {
      localStorage.setItem("themePreference", preference)
    } catch (e) {
      console.warn("Could not save theme to localStorage:", e)
    }
    this.preferenceValue = preference
    this.applyTheme()
    this.updateUI()
    this.persistToServer(preference)
    this.dispatch("changed", { detail: { preference, theme: this.getCurrentTheme() } })
    window.dispatchEvent(new CustomEvent("theme-toggle:changed", { detail: { preference, theme: this.getCurrentTheme() } }))
  }

  applyTheme() {
    const theme = this.resolveTheme()
    document.documentElement.setAttribute("data-bs-theme", theme)
  }

  resolveTheme() {
    const preference = this.getPreference()
    if (preference === "system") {
      return this.getSystemTheme()
    }
    return preference
  }

  getPreference() {
    try {
      const stored = localStorage.getItem("themePreference")
      if (stored) return stored
    } catch (e) {
      console.warn("Could not read theme from localStorage:", e)
    }

    const serverPref = document.documentElement.dataset.userThemePreference
    if (serverPref) return serverPref

    return "system"
  }

  getCurrentTheme() {
    return document.documentElement.getAttribute("data-bs-theme") || "light"
  }

  getSystemTheme() {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }

  handleSystemChange() {
    if (this.getPreference() === "system") {
      this.applyTheme()
      this.updateUI()
      window.dispatchEvent(new CustomEvent("theme-toggle:changed", { detail: { preference: "system", theme: this.getCurrentTheme() } }))
    }
  }

  updateUI() {
    const theme = this.getCurrentTheme()
    const preference = this.getPreference()

    if (this.hasIconTarget) {
      this.iconTargets.forEach(icon => {
        icon.classList.remove("ph-sun", "ph-moon", "ph-circle-half")
        icon.classList.remove("ph", "ph-fill")
        icon.classList.add("ph-fill")
        if (preference === "system") {
          icon.classList.add("ph-circle-half")
        } else if (theme === "dark") {
          icon.classList.add("ph-moon")
        } else {
          icon.classList.add("ph-sun")
        }
      })
    }

    if (this.hasTextTarget) {
      this.textTargets.forEach(text => {
        text.textContent = this.constructor.THEME_LABELS[preference] || "Auto"
      })
    }

    if (this.hasOptionTarget) {
      this.optionTargets.forEach(option => {
        const optionPref = option.dataset.themePreference
        const isActive = optionPref === preference
        option.classList.toggle("active", isActive)
        const checkIcon = option.querySelector("[data-check]")
        if (checkIcon) {
          checkIcon.classList.toggle("d-none", !isActive)
        }
      })
    }
  }

  async persistToServer(preference) {
    const userId = document.body.dataset.userId
    if (!userId) return

    try {
      const response = await post(`/users/${userId}/set_theme_preference`, {
        body: JSON.stringify({ theme_preference: preference }),
        contentType: "application/json",
        responseKind: 'json',
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.statusCode}`)
      }
    } catch (error) {
      console.error("Failed to persist theme preference:", error)
      if (window.showToast) {
        window.showToast("Theme preference could not be saved", "bg-warning")
      }
    }
  }
}
